/**
 * knowledge-effects.ts — v0M9.9 (2026-07-24).
 *
 * Runtime interpreters for the free-text `effects` string on each
 * KnowledgeSpec. As more effects get wired, new helpers land here so
 * consumers (apply-commands, ai, economy, ...) all share one source of
 * truth for "what does this knowledge do at runtime".
 *
 * Design notes:
 *   - Parsing is regex-based against Michael's authored strings. If he
 *     changes the phrasing in the xlsx, the regex here needs to update
 *     in lockstep. This is deliberate — keeps the balance data in the
 *     xlsx while the runtime semantics stay in code (per §6.1 #7).
 *   - Helpers are pure functions of (faction, KNOWLEDGE). No RNG, no
 *     state mutation. Safe to call per-tick per-consumer.
 *   - Base values (uncontested dials) live in the callers; this module
 *     only computes the *bonus* to add.
 *
 * Currently wired:
 *   - Max Army +N (Logistics / Command and Control chain).
 *
 * Not yet wired (Michael to prioritize):
 *   - Cohort Recruit Speed -X% (Settlements / Improved Training Techniques)
 *   - Unlocks Structure: X (Engineering / Espionage / Settlements roots)
 *   - Unlocks Ability: X (post-root Engineering + Espionage nodes)
 *   - Unlock <Order/Formation> (Strategic + Tactical Warfare)
 */

import type { Faction } from './state';
import type { Knowledge } from '../data/knowledge';

/** Matches "Increase Max Army + 3" style effect strings. Case-insensitive
 *  on the leading text; digits captured. */
const MAX_ARMY_BONUS_RE = /Max\s+Army\s*\+\s*(\d+)/i;

/** Matches "Cohort Recruit Speed -20%" style. Sign consumed; digits
 *  captured. Used by the Improved Training Techniques chain in the
 *  Settlements category. */
const RECRUIT_SPEED_RE = /Cohort\s+Recruit\s+Speed\s*[-−]\s*(\d+)\s*%/i;

/** Matches "Unlock Strong Right Flank Formation" — captures the middle
 *  portion (humanized formation name). Consumed by battle-command-modal
 *  to gate formation-picker buttons. */
const UNLOCK_FORMATION_RE = /Unlock\s+(.+?)\s+Formation/i;

/**
 * Sum the "Max Army +N" bonuses across a faction's researched knowledges.
 * Returns 0 for a faction with no research (default state) so callers
 * simply do `baseCap + maxArmyBonus(faction, KNOWLEDGE)`.
 *
 * At v0M9 the Command and Control chain grants tier-N a "+N" bonus,
 * and the chain is linear (each tier requires the previous). Researching
 * all 10 tiers yields 1+2+...+10 = 55. The helper doesn't cap or clamp —
 * balance is Michael's job at the xlsx.
 */
export function maxArmyBonus(faction: Faction, knowledge: Knowledge): number {
  const researched = faction.researchedKnowledge;
  if (!researched || researched.length === 0) return 0;
  let bonus = 0;
  for (const name of researched) {
    const spec = knowledge[name];
    if (!spec) continue;
    const m = MAX_ARMY_BONUS_RE.exec(spec.effects);
    if (m) {
      const n = Number(m[1]);
      if (Number.isFinite(n)) bonus += n;
    }
  }
  return bonus;
}

/**
 * Multiplier applied to recruit-queue progress per tick. 1.0 = no bonus
 * (default); 1.25 = 25% faster = "-20% recruit time". Reads the highest
 * "Cohort Recruit Speed -N%" reduction across researched knowledges
 * (SUPERSEDING semantics — the Improved Training Techniques chain is
 * linear, so tier III's -30% subsumes tier II's -20%; researching all
 * three yields the tier-III multiplier, not the product).
 *
 * Formula: multiplier = 1 / (1 - reduction) so -20% → 1.25× progress,
 * -30% → ~1.43× progress. Applied by `economy.ts` in the recruit-queue
 * advance path so both army-cohort and garrison recruits benefit.
 */
export function recruitSpeedMultiplier(faction: Faction, knowledge: Knowledge): number {
  const researched = faction.researchedKnowledge;
  if (!researched || researched.length === 0) return 1;
  let maxReductionPct = 0;
  for (const name of researched) {
    const spec = knowledge[name];
    if (!spec) continue;
    const m = RECRUIT_SPEED_RE.exec(spec.effects);
    if (m) {
      const n = Number(m[1]);
      if (Number.isFinite(n) && n > maxReductionPct) maxReductionPct = n;
    }
  }
  if (maxReductionPct === 0) return 1;
  // Clamp against divide-by-zero / negative in case a future xlsx row
  // exceeds 99% by mistake — degrade to "no bonus" rather than crash.
  const reduction = Math.min(maxReductionPct, 99) / 100;
  return 1 / (1 - reduction);
}

/**
 * v0M9.14 (2026-07-24) — Build a static map from humanized formation
 * name (e.g. "Strong Right Flank", "Hammer and Anvil", "Cannae") to the
 * knowledge that unlocks it. Populated once at module load; consumers
 * (battle-command-modal, apply-commands.set_battle_formation) look up by
 * humanized name.
 *
 * Formations without an unlock entry are always available (e.g. Balanced,
 * Wide Front — the two v0M4.3 baseline templates).
 */
export function buildFormationUnlockMap(knowledge: Knowledge): Map<string, string> {
  const map = new Map<string, string>();
  for (const name of Object.keys(knowledge)) {
    const spec = knowledge[name]!;
    const m = UNLOCK_FORMATION_RE.exec(spec.effects);
    if (m && m[1]) map.set(m[1].trim(), name);
  }
  return map;
}

/**
 * Convenience wrapper: is the given (humanized) formation name unlocked
 * for a faction? Returns true if the formation isn't gated at all, or
 * if the gating knowledge is in the faction's researchedKnowledge.
 */
export function isFormationUnlocked(
  humanizedName: string,
  faction: Faction,
  unlockMap: Map<string, string>,
): boolean {
  const gate = unlockMap.get(humanizedName);
  if (!gate) return true; // Ungated — always available.
  return (faction.researchedKnowledge ?? []).includes(gate);
}
